import React, { useState, useContext, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Minus, Plus, AlertTriangle, ZoomIn, X } from 'lucide-react';
import { UserAuthContext } from '../../UserAuthContext';
import { addToGuestCart } from '../../utils/guestCart';
import { getProductImageUrl, PRODUCT_PLACEHOLDER_URL } from '../../utils/productImages';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ProductCard.css';

const ProductCard = ({ productId, name, brand, price, description, stock, imageUrl }) => {
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { user } = useContext(UserAuthContext);
  const navigate = useNavigate();
  const imageRef = useRef(null);
  const [showZoom, setShowZoom] = useState(false);
  const [lensStyle, setLensStyle] = useState({});
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [modalZoomed, setModalZoomed] = useState(false);
  const [modalZoomPosition, setModalZoomPosition] = useState({ x: 50, y: 50 });
  const [displayImageUrl, setDisplayImageUrl] = useState(() => getProductImageUrl(imageUrl));

  const openZoomModal = useCallback(() => {
    setModalZoomed(false);
    setShowZoomModal(true);
  }, []);

  const closeZoomModal = useCallback(() => {
    setShowZoomModal(false);
    setModalZoomed(false);
  }, []);

  // Log render to detect excessive re-renders
  useEffect(() => {
  }, [productId, name]);

  // Ensure image is loaded before enabling zoom.
  useEffect(() => {
    setDisplayImageUrl(getProductImageUrl(imageUrl));
    setImageLoaded(false);
    setShowZoom(false);
    setLensStyle({});
  }, [imageUrl]);

  useEffect(() => {
    const image = imageRef.current;
    if (!image) return;

    const handleImageLoad = () => {
      setImageLoaded(true);
    };

    if (image.complete && image.naturalWidth > 0) {
      handleImageLoad();
    } else {
      image.addEventListener('load', handleImageLoad);
      return () => image.removeEventListener('load', handleImageLoad);
    }
  }, [displayImageUrl]);

  useEffect(() => {
    if (!showZoomModal) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closeZoomModal();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [closeZoomModal, showZoomModal]);

  const handleImageError = useCallback((event) => {
    event.currentTarget.onerror = null;
    if (displayImageUrl !== PRODUCT_PLACEHOLDER_URL) {
      setImageLoaded(false);
      setDisplayImageUrl(PRODUCT_PLACEHOLDER_URL);
    }
  }, [displayImageUrl]);

  // Zoom functionality
  const handleMouseMove = useCallback((event) => {
    if (!imageLoaded || !imageRef.current) return;

    const image = imageRef.current;
    const rect = image.getBoundingClientRect();
    const containerRect = image.parentElement.getBoundingClientRect();
    const computed = window.getComputedStyle(image);
    const paddingLeft = Number.parseFloat(computed.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(computed.paddingRight) || 0;
    const paddingTop = Number.parseFloat(computed.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(computed.paddingBottom) || 0;
    const contentWidth = Math.max(1, rect.width - paddingLeft - paddingRight);
    const contentHeight = Math.max(1, rect.height - paddingTop - paddingBottom);
    const imageRatio = image.naturalWidth / image.naturalHeight;
    const contentRatio = contentWidth / contentHeight;
    const renderedWidth = imageRatio > contentRatio ? contentWidth : contentHeight * imageRatio;
    const renderedHeight = imageRatio > contentRatio ? contentWidth / imageRatio : contentHeight;
    const renderedLeft = rect.left + paddingLeft + (contentWidth - renderedWidth) / 2;
    const renderedTop = rect.top + paddingTop + (contentHeight - renderedHeight) / 2;

    if (
      event.clientX < renderedLeft || event.clientX > renderedLeft + renderedWidth ||
      event.clientY < renderedTop || event.clientY > renderedTop + renderedHeight
    ) {
      setShowZoom(false);
      return;
    }

    const zoom = 2.5;
    const lensSize = Math.min(144, renderedWidth, renderedHeight);
    const relativeX = (event.clientX - renderedLeft) / renderedWidth;
    const relativeY = (event.clientY - renderedTop) / renderedHeight;

    setLensStyle({
      width: `${lensSize}px`,
      height: `${lensSize}px`,
      left: `${event.clientX - containerRect.left - lensSize / 2}px`,
      top: `${event.clientY - containerRect.top - lensSize / 2}px`,
      backgroundImage: `url("${displayImageUrl.replace(/"/g, '%22')}")`,
      backgroundSize: `${renderedWidth * zoom}px ${renderedHeight * zoom}px`,
      backgroundPosition: `${lensSize / 2 - relativeX * renderedWidth * zoom}px ${lensSize / 2 - relativeY * renderedHeight * zoom}px`
    });
    setShowZoom(true);
  }, [displayImageUrl, imageLoaded]);

  const handleMouseLeave = useCallback(() => {
    setShowZoom(false);
  }, []);

  const handleModalPointerMove = useCallback((event) => {
    if (!modalZoomed) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setModalZoomPosition({
      x: Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100))
    });
  }, [modalZoomed]);

  const toggleModalZoom = useCallback((event) => {
    if (!modalZoomed) {
      const rect = event.currentTarget.getBoundingClientRect();
      setModalZoomPosition({
        x: Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100)),
        y: Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100))
      });
    }
    setModalZoomed((value) => !value);
  }, [modalZoomed]);

  const handleIncrement = useCallback(() => {
    setQuantity(prev => prev + 1);
  }, []);

  const handleDecrement = useCallback(() => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  }, [quantity]);

  const handleQuantityChange = useCallback((e) => {
    const newQuantity = parseInt(e.target.value, 10);
    if (!isNaN(newQuantity) && newQuantity >= 1) {
      setQuantity(newQuantity);
    } else {
      setQuantity(1);
    }
  }, []);

  const handleAddToCart = useCallback(async () => {
    if (!user) {
      addToGuestCart({
        idproducts: parseInt(productId, 10),
        product_name: name || 'Unknown Product',
        product_description: description || 'No description',
        product_price: parseFloat(price) || 0,
        product_image: (imageUrl || '')?.replace('https://valleyapi-dr9y.onrender.com/data/', ''),
        product_brand: brand || 'Unknown',
        product_stock: stock,
        quantity,
      });
      toast.success(`Added ${name || 'product'} to cart!`, {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      return;
    }

    const token = localStorage.getItem('user_token');
    if (!token) {
      toast.error('Session expired. Please log in again.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      // Send the user home and auto-open the header's login panel instead of
      // the registration form (there is no dedicated /login route).
      try { sessionStorage.setItem('valley_open_auth', '1'); } catch { /* storage unavailable */ }
      navigate('/');
      return;
    }

    if (!productId) {
      toast.error('Invalid product ID.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      return;
    }

    const formattedPrice = parseFloat(price) || 0;
    const truncatedImage = (imageUrl || null)?.replace('https://valleyapi-dr9y.onrender.com/data/', '');

    const cartItem = {
      idusers: user.userid,
      idproducts: parseInt(productId, 10),
      product_name: name || 'Unknown Product',
      product_description: description || 'No description',
      product_price: formattedPrice,
      product_image: truncatedImage,
      product_brand: brand || 'Unknown',
      quantity,
    };

    setIsLoading(true);
    try {
      const response = await fetch('https://valleyapi-dr9y.onrender.com/api/cart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(cartItem),
      });

      const responseData = await response.json();
      if (response.ok) {
        toast.success(`Added ${name || 'product'} to cart!`, {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          toastId: `add-to-cart-${productId}`,
        });
        window.dispatchEvent(new Event('cartUpdated'));
      } else {
        toast.error(`Failed to add to cart: ${responseData.message || 'Unknown error'}`, {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
        });
      }
    } catch (error) {
      toast.error('Error adding to cart. Please try again.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, navigate, productId, name, description, price, imageUrl, brand, stock, quantity]);

  const isOutOfStock = stock === 0;
  const isLowStock = stock > 0 && stock <= 5;
  const hasPrice = Number.isFinite(price) && price > 0;

  return (
    <>
    <article className="product-card-container">
      <div className="product-card-image-wrapper">
        <div className="product-card-image-label">Product gallery</div>
        <div
          className="product-card-image-container"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={openZoomModal}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openZoomModal();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`Open zoom for ${name}`}
        >
          <img
            ref={imageRef}
            src={displayImageUrl}
            alt={name}
            className="product-card-main-image"
            onLoad={() => setImageLoaded(true)}
            onError={handleImageError}
          />
          {showZoom && (
            <div className="product-card-zoom-lens" style={lensStyle} aria-hidden="true" />
          )}
          <span className="product-card-zoom-hint"><ZoomIn size={16} /> View larger</span>
        </div>
        {isLowStock && (
          <div className="product-card-status-badge product-card-badge-low-stock">
            <AlertTriangle className="w-4 h-4" />
            Low Stock
          </div>
        )}
        {isOutOfStock && (
          <div className="product-card-status-badge product-card-badge-out-of-stock">
            No Stock
          </div>
        )}
      </div>

      <div className="product-card-main-content">
        <header className="product-card-header-section">
          <p className="product-card-product-brand">{brand || 'Industrial equipment'}</p>
          <h1 className="product-card-product-title">{name}</h1>
          <div className={`product-card-availability ${isOutOfStock ? 'is-unavailable' : 'is-available'}`}>
            <i aria-hidden="true" />
            {isOutOfStock ? 'Available on request' : `${stock} ${stock === 1 ? 'unit' : 'units'} in stock`}
          </div>
        </header>

        <section className="product-card-description-section" aria-labelledby={`product-overview-${productId}`}>
          <h2 id={`product-overview-${productId}`} className="product-card-description-title">Product overview</h2>
          <p className="product-card-description-text">{description}</p>
        </section>

        <footer className="product-card-footer-section">
          <div className="product-card-price-row">
            <div>
              <span className="product-card-price-label">Unit price</span>
              <div className={`product-card-product-price ${hasPrice ? '' : 'is-pending'}`}>
                {hasPrice ? `$${price.toFixed(2)}` : 'Price on request'}
              </div>
            </div>
            <span className="product-card-stock-info">
              {!hasPrice ? 'Add to cart to request pricing' : stock > 0 ? 'Available for immediate ordering' : 'Contact us for availability'}
            </span>
          </div>

          <div className="product-card-action-controls">
            <div className="product-card-quantity-section">
              <label htmlFor={`quantity-${productId}`} className="product-card-quantity-label">Quantity</label>
              <div className="product-card-quantity-wrapper">
                <button
                  type="button"
                  onClick={handleDecrement}
                  className="product-card-quantity-button"
                  disabled={isLoading || quantity <= 1}
                  aria-label="Decrease quantity"
                >
                  <Minus />
                </button>
                <input
                  type="number"
                  id={`quantity-${productId}`}
                  value={quantity}
                  onChange={handleQuantityChange}
                  className="product-card-quantity-field"
                  disabled={isLoading}
                  min="1"
                />
                <button
                  type="button"
                  onClick={handleIncrement}
                  className="product-card-quantity-button"
                  disabled={isLoading}
                  aria-label="Increase quantity"
                >
                  <Plus />
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={handleAddToCart}
              className="product-card-cart-button"
              disabled={isLoading}
              aria-label={`Add ${name} to cart`}
            >
              {isLoading ? (
                <span className="product-card-loading-state">
                  <span className="product-card-loading-spinner" />
                  Adding...
                </span>
              ) : (
                <><ShoppingCart /> Add to cart</>
              )}
            </button>
          </div>
        </footer>
      </div>
    </article>
    {showZoomModal && (
      <div className="product-card-zoom-modal" role="dialog" aria-modal="true" aria-label={`${name} image zoom`} onClick={closeZoomModal}>
        <button type="button" className="product-card-zoom-close" onClick={closeZoomModal} aria-label="Close image zoom"><X /></button>
        <div
          className={`product-card-zoom-stage ${modalZoomed ? 'is-zoomed' : ''}`}
          onClick={(event) => event.stopPropagation()}
          onMouseMove={handleModalPointerMove}
        >
          <img
            src={displayImageUrl}
            alt={`${name} enlarged view`}
            className={modalZoomed ? 'is-zoomed' : ''}
            style={{ transformOrigin: `${modalZoomPosition.x}% ${modalZoomPosition.y}%` }}
            onLoad={() => setImageLoaded(true)}
            onError={handleImageError}
            onClick={toggleModalZoom}
          />
          <p>{modalZoomed ? 'Click the image to fit it to the screen' : 'Click the image for 2× zoom'}</p>
        </div>
      </div>
    )}
    </>
  );
};

export default React.memo(ProductCard);
